<script setup>
import { ref, computed, inject, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ArrowRight } from '@element-plus/icons-vue'
import BaseCom from './BaseCom.vue'
import eventBus from '../utils/eventBus'

// 获取路由实例
const router = useRouter()

// 定义组件属性
const props = defineProps({
  contentId: {
    type: String,
    default: ''
  }
})

// 定义emits
const emit = defineEmits(['edit', 'click'])

// 组件类型
const comType = 'button'
// 给BaseCom组件添加ref引用
const baseComRef = ref(null)

// 默认配置
const defaultVal = {
  zh: '按钮',
  en: 'Button',
  color: '#409eff',
  textColor: 'white',
  url: '',
  target: '_self',
  disabled: false,
  size: 'medium', // 添加尺寸属性，可选值：small, medium, large
  hidden: true
}

// 注入外部依赖
const contentMap = inject('contentMap', {})
const isLoggedIn = inject('isLoggedIn', false)
const currentLanguage = inject('currentLanguage', 'zh')
const handleButtonComEdit = inject('handleButtonComEdit', null)

// 配置响应式变量
const config = ref(defaultVal)

// 添加watch监听器，监听contentId变化
watch(contentMap, (newValue) => {
  config.value = newValue[props.contentId] || defaultVal
}, { immediate: true, deep: true })

// 订阅按钮更新事件
const unsubscribeButtonUpdate = eventBus.on('button-updated', (data) => {
  // 只有当更新的数据与当前组件ID匹配时才更新
  if (data.contentId === props.contentId) {
    config.value = { ...config.value, ...data.config }
    console.log(`ButtonCom ${props.contentId} updated via EventBus`, config.value)
  }
})

// 处理按钮点击事件
const handleClick = (event) => {
  // 触发点击事件
  emit('click', event)
  
  // 如果配置了url，则进行跳转
  if (config.value.href) {
    const url = config.value.href
    
    // 检查URL类型并选择跳转方式
    if (url.startsWith('/')) {
      // 内部路由，使用router.push
      router.push(url)
      const targetElement = document.querySelector('.navbar');
      if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } else if (url.startsWith('http')) {
      // 外部链接，在新窗口打开
      window.open(url, '_blank')
    }
  }
}

// 计算按钮样式
const buttonStyle = computed(() => {
  // 基础样式
  const baseStyle = {
    backgroundColor: config.value.color,
    color: config.value.textColor || 'white'
  }
  
  // 根据尺寸添加动态样式
  const sizeStyle = {
    // 小按钮
    small: {
      padding: '6px 12px',
      fontSize: '12px',
      height: '1.4375vw',
      gap: '10px'
    },
    // 中按钮
    medium: {
      padding: '10px 20px',
      fontSize: '14px',
      height: '2.4375vw',
      gap: '20px'
    },
    // 大按钮
    large: {
      padding: '14px 28px',
      fontSize: '16px',
      height: '3.4375vw',
      gap: '30px',
      borderRadius: '40px'
    }
  }
  
  return {
    ...baseStyle,
    ...sizeStyle[config.value.size || 'medium']
  }
})

// 无需动画样式和类

// 处理编辑事件
const handleEdit = () => {
  // 发布编辑事件到EventBus
  eventBus.emit('button-edit-request', {
    config: config.value,
    contentId: props.contentId || `button-${Date.now()}`,
    type: comType
  });
  
  // 保留原有逻辑作为备用
  if (handleButtonComEdit) {
    handleButtonComEdit(props.contentId)
  } else {
    // 备用方案：发出edit事件
    emit('edit', { data: config.value, type: comType, id: props.contentId })
  }
}

// 处理BaseCom组件的鼠标移入事件
const handleBaseComMouseEnter = () => {
  // 当ButtonCom的BaseCom组件鼠标移入时，通知父级ContainerCom的BaseCom实例
  if (baseComRef.value) {
    const parentBaseCom = baseComRef.value.$parent?.$parent
    if (parentBaseCom?.hideEditButton) {
      parentBaseCom.hideEditButton()
    }
  }
}

// 只保留unsubscribe
const unsubscribe = () => {
  unsubscribeButtonUpdate()
}
</script>

<template>
  <BaseCom
    
    ref="baseComRef"
    :editable="isLoggedIn"
    :contentId="contentId"
    @edit="handleEdit"
    @mouseenter="handleBaseComMouseEnter"
  >
    <!-- 按钮元素 -->
    <button
    v-show="!config.hidden"
      class="button-com"
      :class="`button-${config.size || 'medium'}`"
      :style="buttonStyle"
      @click="handleClick"
      :disabled="config.disabled || false"
    >
      {{ config[currentLanguage] || '按钮' }}
      <el-icon><ArrowRight /></el-icon>
    </button>
  </BaseCom>
</template>

<style scoped>
/* 确保按钮内容正确显示 */
:deep(.content-area) {
  min-height: auto;
  display: inline-block;
  position: relative;
  z-index: 1;
}

/* 确保遮罩层显示在最上层 */
:deep(.hover-mask-container) {
  min-height: 40px; /* 默认中等高度 */
  position: relative;
  z-index: 10; /* 提高遮罩层的z-index */
}

/* 尺寸相关样式调整 */
.button-small :deep(.hover-mask-container) {
  min-height: 32px;
}

.button-large :deep(.hover-mask-container) {
  min-height: 48px;
}

/* 调整图标的大小以匹配按钮尺寸 */
.button-small .el-icon {
  transform: scale(0.8);
}

.button-large .el-icon {
  transform: scale(1.2);
}

/* 按钮基础样式 */
.button-com {
  /* 基础样式 */
  /* border: 1px solid #ccc; */
  border-radius: 20px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  outline: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  position: relative;
  z-index: 1;
  margin: 5px;
  
  /* 禁用状态 */
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  /* 悬停效果 */
  &:not(:disabled):hover {
    opacity: 0.9;
    transform: translateY(-5px);
    /* box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15); */
  }
  
  /* 点击效果 */
  &:not(:disabled):active {
    transform: translateY(0);
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  }
}

/* Element Plus箭头图标样式 */
.el-icon {
  color: inherit;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

/* 额外确保遮罩层在最上层 */
:deep(.hover-mask) {
  position: absolute;
  z-index: 100; /* 进一步提高遮罩层的z-index */
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}
</style>